// frontend/modules/in-app-notifications/views/InAppNotificationListView.tsx
import React, { useState } from 'react';
import { Card, Typography, Space, Button, Select, DatePicker, Input, Row, Col, Statistic } from 'antd';
import { 
    ReloadOutlined, 
    FilterOutlined, 
    CheckOutlined,
    PlusOutlined 
} from '@ant-design/icons';
import { useInAppNotifications } from '../hooks/useInAppNotifications';
import { NotificationList } from '../components/NotificationList';
import { NotificationFilters } from '../types';
import { useTheme } from '@/components/common/ThemeProvider';
import { useNavigate } from 'react-router-dom';

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

export const InAppNotificationListView: React.FC = () => {
    const navigate = useNavigate();
    const { dark } = useTheme();
    const [showFilters, setShowFilters] = useState(false);
    
    const {
        notifications,
        stats,
        filters,
        isLoading,
        currentPage,
        pageSize,
        totalElements,
        totalPages,
        updateFilters,
        goToPage,
        changePageSize,
        refreshNotifications,
        markAsRead,
        markAsAttended,
        markAllAsRead,
        deleteNotification,
        isMarkingAllAsRead
    } = useInAppNotifications();

    const handleFilterChange = (key: keyof NotificationFilters, value: any) => {
        updateFilters({
            ...filters,
            [key]: value
        });
    };

    const handleDateRangeChange = (dates: any) => {
        if (dates && dates.length === 2) {
            updateFilters({
                ...filters,
                dateFrom: dates[0].format('YYYY-MM-DD'),
                dateTo: dates[1].format('YYYY-MM-DD')
            });
        } else {
            updateFilters({
                ...filters,
                dateFrom: undefined,
                dateTo: undefined
            });
        }
    };

    const clearFilters = () => {
        updateFilters({});
    };

    const handleNotificationClick = (notification: any) => {
        navigate(`/notifications/${notification.id}`);
    };

    return (
        <div className={`min-h-screen p-6 ${dark ? 'bg-gray-900 text-gray-100' : 'bg-gray-100 text-gray-800'}`}>
            <div className="container mx-auto max-w-6xl">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <Title level={2} className={`mb-0 ${dark ? 'text-white' : ''}`}>
                        Notifications
                    </Title>
                    <Space>
                        <Button
                            icon={<FilterOutlined />}
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            Filters
                        </Button>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={refreshNotifications}
                            loading={isLoading}
                        >
                            Refresh
                        </Button>
                        {stats && stats.unreadCount > 0 && (
                            <Button
                                type="primary"
                                icon={<CheckOutlined />}
                                onClick={markAllAsRead}
                                loading={isMarkingAllAsRead}
                            >
                                Mark All Read ({stats.unreadCount})
                            </Button>
                        )}
                    </Space>
                </div>

                {/* Statistics */}
                {stats && (
                    <Row gutter={16} className="mb-6">
                        <Col xs={6}>
                            <Card className={dark ? 'bg-gray-800 border-gray-700' : ''}>
                                <Statistic
                                    title="Total"
                                    value={stats.totalCount}
                                    valueStyle={{ color: dark ? '#fff' : '#1890ff' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={6}>
                            <Card className={dark ? 'bg-gray-800 border-gray-700' : ''}>
                                <Statistic
                                    title="Unread"
                                    value={stats.unreadCount}
                                    valueStyle={{ color: '#f5222d' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={6}>
                            <Card className={dark ? 'bg-gray-800 border-gray-700' : ''}>
                                <Statistic
                                    title="Attended"
                                    value={stats.attendedCount}
                                    valueStyle={{ color: '#52c41a' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={6}>
                            <Card className={dark ? 'bg-gray-800 border-gray-700' : ''}>
                                <Statistic
                                    title="Expired"
                                    value={stats.expiredCount}
                                    valueStyle={{ color: '#faad14' }}
                                />
                            </Card>
                        </Col>
                    </Row>
                )}

                {/* Filters */}
                {showFilters && (
                    <Card className={`mb-6 ${dark ? 'bg-gray-800 border-gray-700' : ''}`}>
                        <Row gutter={16}>
                            <Col xs={24} sm={8}>
                                <label className="block text-sm font-medium mb-2">
                                    Status
                                </label>
                                <Select
                                    placeholder="All notifications"
                                    value={filters.unreadOnly}
                                    onChange={(value) => handleFilterChange('unreadOnly', value)}
                                    className="w-full"
                                    allowClear
                                >
                                    <Option value={true}>Unread only</Option>
                                    <Option value={false}>Read only</Option>
                                </Select>
                            </Col>
                            <Col xs={24} sm={8}>
                                <label className="block text-sm font-medium mb-2">
                                    Category
                                </label>
                                <Select
                                    placeholder="Filter by category"
                                    allowClear
                                    value={filters.category}
                                    onChange={(value) => handleFilterChange('category', value)}
                                    style={{ width: 200 }}
                                >
                                </Select>
                            </Col>
                            <Col xs={24} sm={8}>
                                <label className="block text-sm font-medium mb-2">
                                    Date Range
                                </label>
                                <RangePicker
                                    onChange={handleDateRangeChange}
                                    className="w-full"
                                />
                            </Col>
                        </Row>
                        <div className="flex justify-end mt-4">
                            <Button onClick={clearFilters}>
                                Clear Filters
                            </Button>
                        </div>
                    </Card>
                )}

                {/* Notifications List */}
                <Card className={dark ? 'bg-gray-800 border-gray-700' : ''}>
                    <NotificationList
                        notifications={notifications}
                        loading={isLoading}
                        showPagination={true}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={(page, size) => {
                            goToPage(page);
                            if (size !== pageSize) {
                                changePageSize(size);
                            }
                        }}
                        onNotificationClick={handleNotificationClick}
                        onMarkAsRead={markAsRead}
                        onMarkAsAttended={markAsAttended}
                        onDelete={deleteNotification}
                    />
                </Card>
            </div>
        </div>
    );
};
